import { useQuery } from '@tanstack/react-query';
import { salesApi } from '../services/api';
import { DailySalesResponse, DateRangeSalesResponse, PeriodComparison } from '../types';

export const useDailySales = (date?: string, storeId?: string) => {
  return useQuery<DailySalesResponse>({
    queryKey: ['dailySales', date, storeId],
    queryFn: async () => {
      const response = await salesApi.getDailySales(date, storeId);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useDateRangeSales = (startDate: string, endDate: string, storeId?: string) => {
  return useQuery<DateRangeSalesResponse>({
    queryKey: ['dateRangeSales', startDate, endDate, storeId],
    queryFn: async () => {
      const response = await salesApi.getDateRangeSales(startDate, endDate, storeId);
      return response.data;
    },
    enabled: !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useMonthToDateSales = (date?: string, storeId?: string) => {
  return useQuery<PeriodComparison>({
    queryKey: ['mtdSales', date, storeId],
    queryFn: async () => {
      const response = await salesApi.getMonthToDateSales(date, storeId);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useWeekToDateSales = (date?: string, storeId?: string) => {
  return useQuery<PeriodComparison>({
    queryKey: ['wtdSales', date, storeId],
    queryFn: async () => {
      const response = await salesApi.getWeekToDateSales(date, storeId);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useYearToDateSales = (date?: string, storeId?: string) => {
  return useQuery<PeriodComparison>({
    queryKey: ['ytdSales', date, storeId],
    queryFn: async () => {
      const response = await salesApi.getYearToDateSales(date, storeId);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useTrailing12Months = (date?: string, storeId?: string) => {
  return useQuery<any>({
    queryKey: ['trailing12Months', date, storeId],
    queryFn: async () => {
      const response = await salesApi.getTrailing12Months(date, storeId);
      return response.data;
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};